import { message } from 'antd';
import { AnimatedSprite, Container, Graphics, InteractionEvent, Loader, Sprite, Text } from 'pixi.js';
import PeerStore from '../class/PeerStore';
import { Game, intersectionLL, Scene, Store, TextureFactory } from '../core';
import { generateCode, linear } from '../util';
import MainScene from './MainScene';
import DrawingPlayerTextureInfo_1 from '../class/drawing/Player_1.json';
import { LOCALE_DATA } from '../constant';
import { Player as JumpGamePlayer, MultiplayScaffolding } from '../class/jumping';
import JumpingGameProgress from '../ui/JumpingGameProgress';
import { Line, Point } from '../interface';
import seedrandom from 'seedrandom';
import { loadGoogleAdvertisement, showGoogleAdvertisement } from '../util/AD';
import VanishTitle from '../ui/VanishTitle';
import { BlackFrameCutAway } from '../cutaway';
import { sound } from '@pixi/sound';
import EmotionData from '../class/Emotion.json';
import Emotion from '../class/Emotion';
import { logging, LOG_EVENT, LOG_SCENE } from '../api/log';

const enum PLAYER_STATE {
  WAIT,
  READY,
  DISCONNECTED,
}

const enum GAME_TYPE {
  DRAWING_GAME,
  JUMPING_GAME,
}

const enum MULTIPLAY_ROOM_STATE {
  WAIT,
  LOADING_INITIALIZE,
  LOADING,
  LOADED,
  PLAYING,
  END,
  CLOSING,
}

interface PlayerState {
  name: string;
  state: PLAYER_STATE;
  type: GAME_TYPE;
  initialized: boolean;
  seed: string;
  updatedAt: number;
  adViewed: boolean;
}

class ResultPopUp extends Container {
  private text: Text;
  private ninimoSprite: AnimatedSprite;

  constructor() {
    super();

    const dim = new Graphics();
    dim.beginFill(0x000000, 0.7);
    dim.drawRect(-540, -960, 1080, 1920);
    dim.endFill();
    this.addChild(dim);

    const sprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/result.png'));
    sprite.anchor.set(0.5);
    this.addChild(sprite);

    this.text = new Text('User Win!', {
      fontSize: 70,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0xffffff',
      stroke: '0x492A2E',
      strokeThickness: 16,
      lineJoin: 'round',
      padding: 20,
    });
    this.text.y = -390;
    this.text.anchor.set(0.5);
    this.addChild(this.text);

    this.ninimoSprite = new AnimatedSprite([
      TextureFactory.instance.getSingleTexture('/textures/ui/ninimo_1.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/ninimo_2.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/ninimo_3.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/ninimo_4.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/ninimo_5.png'),
    ]);
    this.ninimoSprite.gotoAndStop(4);
    this.ninimoSprite.anchor.set(0.5);
    this.ninimoSprite.y = -100;
    this.addChild(this.ninimoSprite);

    let retryButtonPressed = false;
    const retryButton = new LongButton(LOCALE_DATA[Store.instance.state.locale].TRY_AGIN);
    retryButton.y = 250;
    this.addChild(retryButton);
    retryButton.interactive = true;
    retryButton.buttonMode = true;
    retryButton.on('pointerdown', () => {
      retryButtonPressed = true;
      retryButton.scale.set(retryButton.scale.x * 0.95);
    });
    retryButton.on('pointerup', () => {
      if (retryButtonPressed) {
        sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
        retryButton.scale.set(retryButton.scale.x / 0.95);
        this.emit('retry');
      }
      retryButtonPressed = false;
    });
    retryButton.on('pointerupoutside', () => {
      retryButtonPressed = false;
      retryButton.scale.set(retryButton.scale.x / 0.95);
    });

    let homeButtonPressed = false;
    const homeButton = new LongButton(LOCALE_DATA[Store.instance.state.locale].GO_HOME);
    homeButton.y = 400;
    this.addChild(homeButton);
    homeButton.interactive = true;
    homeButton.buttonMode = true;
    homeButton.on('pointerdown', () => {
      homeButtonPressed = true;
      homeButton.scale.set(homeButton.scale.x * 0.95);
    });
    homeButton.on('pointerup', () => {
      if (homeButtonPressed) {
        sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
        homeButton.scale.set(homeButton.scale.x / 0.95);
        this.emit('home');
      }
      homeButtonPressed = false;
    });
    homeButton.on('pointerupoutside', () => {
      homeButtonPressed = false;
      homeButton.scale.set(homeButton.scale.x / 0.95);
    });
  }

  public changeState(state: string) {
    if (state === 'WIN') {
      this.text.text = `${Store.instance.data.playername} Win!`;
      this.ninimoSprite.gotoAndStop(4);
    } else {
      this.text.text = `${Store.instance.data.playername} Lose...`;
      this.ninimoSprite.gotoAndStop(Math.floor(Math.random() * 4));
    }
  }
}

class LongButton extends Container {
  public title: Text;

  constructor(text: string) {
    super();

    const btn = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/btn_long.png'));
    btn.anchor.set(0.5);
    this.addChild(btn);

    this.title = new Text(text, {
      fontSize: 65,
      fontFamily: 'gaegu',
      fontWeight: '400',
      fontStyle: 'normal',
      fill: '0x492A2E',
      lineJoin: 'round',
      padding: 10,
    });
    this.title.y = -5;
    this.title.anchor.set(0.5);
    this.addChild(this.title);
  }
}

class PlayerSlot extends Container {
  public playerName: Text;
  public stateText: Text;

  constructor() {
    super();
    const textures = TextureFactory.instance.load(DrawingPlayerTextureInfo_1);
    const sprite = new AnimatedSprite(textures.slice(174, 187));

    sprite.anchor.set(0.45, 0.55);
    sprite.scale.set(0.9);
    sprite.animationSpeed = 0.3;
    sprite.play();

    this.addChild(sprite);

    this.playerName = new Text('', {
      fontSize: 65,
      fontWeight: '900',
      fontFamily: 'gaegu',
      fontStyle: 'normal',
      lineJoin: 'round',
      letterSpacing: -4,
      fill: '#4a282f',
      stroke: 0xffffff,
      strokeThickness: 12,
      padding: 12,
    });
    this.playerName.anchor.set(0.5);
    this.playerName.position.set(320, -53);
    this.addChild(this.playerName);

    this.stateText = new Text('- WAIT -', {
      fontSize: 75,
      fontWeight: '900',
      fontFamily: 'gaegu',
      fontStyle: 'normal',
      lineJoin: 'round',
      letterSpacing: -4,
      fill: '#ff0000',
      padding: 8,
    });
    this.stateText.anchor.set(0.5);
    this.stateText.position.set(320, 25);
    this.addChild(this.stateText);
  }
}

class MultiplayerStateStage extends Container {
  public players: Record<string, PlayerState> = {};
  public playerSlots: Array<PlayerSlot> = [];
  public gameTitle!: Text;
  public gameThumbnail!: Sprite;
  public btnReady!: LongButton;

  constructor() {
    super();
    this.initialize();
  }

  private initialize(): void {
    const bgMultiplay = new Sprite(TextureFactory.instance.getSingleTexture('/textures/bg/bg_multiplay.png'));
    this.addChild(bgMultiplay);

    const popup = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/popup_name_change.png'));
    popup.anchor.set(0.5, 0);
    popup.position.set(540, -100);
    this.addChild(popup);

    const title = new Text('Players', {
      fontSize: 100,
      fontWeight: '900',
      fontFamily: 'gaegu',
      fontStyle: 'normal',
      letterSpacing: -4,
      fill: '#4a282f',
      stroke: 0xffffff,
      strokeThickness: 20,
      lineJoin: 'round',
      padding: 8,
    });
    title.anchor.set(0.5);
    title.position.set(540, 535);
    this.addChild(title);

    this.gameThumbnail = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/game_2.png'));
    const mask = new Graphics();
    mask.beginFill(0xffffff);
    mask.drawRoundedRect(195, 1040, 690, 380, 30);
    mask.endFill();
    this.addChild(mask);

    this.gameThumbnail.width = 690;
    this.gameThumbnail.height = 380;
    this.gameThumbnail.anchor.set(0.5);
    this.gameThumbnail.position.set(540, 1230);
    this.gameThumbnail.mask = mask;
    this.gameThumbnail.filters = [];
    this.addChild(this.gameThumbnail);

    // const leftButton = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/btn_arrow.png'));
    // leftButton.anchor.set(0.5);
    // leftButton.position.set(195, 1230);
    // leftButton.scale.set(-0.5, 0.5);
    // leftButton.interactive = true;
    // leftButton.buttonMode = true;
    // leftButton.visible = PeerStore.instance.peer.isHost;
    // leftButton.on('pointerdown', () => {
    //   sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
    //   this.emit('left');
    // });
    // this.addChild(leftButton);

    // const rightButton = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/btn_arrow.png'));
    // rightButton.anchor.set(0.5);
    // rightButton.position.set(885, 1230);
    // rightButton.scale.set(0.5, 0.5);
    // rightButton.interactive = true;
    // rightButton.buttonMode = true;
    // rightButton.visible = PeerStore.instance.peer.isHost;
    // rightButton.on('pointerdown', () => {
    //   sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
    //   this.emit('right');
    // });
    // this.addChild(rightButton);

    this.gameTitle = new Text(LOCALE_DATA[Store.instance.state.locale].JUMPING_GAME, {
      fontSize: 80,
      fontWeight: '900',
      fontFamily: 'gaegu',
      fontStyle: 'normal',
      letterSpacing: -4,
      fill: '#4a282f',
      stroke: 0xffffff,
      strokeThickness: 20,
      lineJoin: 'round',
      padding: 8,
    });
    this.gameTitle.anchor.set(0.5);
    this.gameTitle.position.set(540, 1350);
    this.addChild(this.gameTitle);

    this.btnReady = new LongButton(
      PeerStore.instance.peer.isHost
        ? LOCALE_DATA[Store.instance.state.locale].START
        : LOCALE_DATA[Store.instance.state.locale].READY
    );
    let pressed: boolean = false;
    this.btnReady.position.set(540, 1545);
    this.btnReady.interactive = true;
    this.btnReady.buttonMode = true;
    this.btnReady.visible = !PeerStore.instance.peer.isHost;
    this.btnReady.on('pointerdown', () => {
      pressed = true;
      this.btnReady.scale.set(this.btnReady.scale.x * 0.95);
    });
    this.btnReady.on('pointerup', () => {
      if (pressed) {
        sound.play('/audio/btn_start.mp3', { loop: false, volume: Store.instance.state.volume as number });
        this.btnReady.scale.set(this.btnReady.scale.x / 0.95);
        this.emit('ready');
      }
      pressed = false;
    });
    this.btnReady.on('pointerupoutside', () => {
      pressed = false;
      this.btnReady.scale.set(this.btnReady.scale.x / 0.95);
    });
    this.addChild(this.btnReady);
  }

  public update(elapsed: number): void {}

  public updateState(): void {
    for (let i = 0; i < Object.keys(this.players).length; i++) {
      if (!this.playerSlots[i]) {
        const slot = new PlayerSlot();
        slot.x = 360;
        slot.y = 730 + i * 165;
        this.addChild(slot);
        this.playerSlots.push(slot);
      }
      const code = Object.keys(this.players)[i];
      this.playerSlots[i].playerName.text = this.players[code].name;

      if (this.players[code].state === PLAYER_STATE.READY) {
        this.playerSlots[i].stateText.text = '- READY -';
        this.playerSlots[i].stateText.style.fill = 0xffc05d;
      } else {
        this.playerSlots[i].stateText.text = '- WAIT -';
        this.playerSlots[i].stateText.style.fill = 0xff505d;
      }
    }

    if (PeerStore.instance.peer.isHost) {
      let isAllReady = true;
      for (let i = 0; i < Object.keys(this.players).length; i++) {
        const code = Object.keys(this.players)[i];
        if (this.players[code].state !== PLAYER_STATE.READY) {
          isAllReady = false;
          break;
        }
      }

      if (isAllReady) {
        this.btnReady.visible = true;
      } else {
        this.btnReady.visible = false;
      }
    } else {
      if (this.players[PeerStore.instance.peer.code!].state === PLAYER_STATE.READY) {
        this.btnReady.title.text = LOCALE_DATA[Store.instance.state.locale].CANCEL;
      } else {
        this.btnReady.title.text = LOCALE_DATA[Store.instance.state.locale].READY;
      }
    }

    for (let i = this.playerSlots.length - 1; i >= Object.keys(this.players).length; i--) {
      this.removeChild(this.playerSlots[i]);
      this.playerSlots[i].destroy();
      this.playerSlots.splice(i, 1);
    }

    if (PeerStore.instance.peer.code) {
      switch (this.players[PeerStore.instance.peer.code].type) {
        case GAME_TYPE.DRAWING_GAME:
          this.gameThumbnail.texture = TextureFactory.instance.getSingleTexture('/textures/ui/game_1.png');
          this.gameThumbnail.texture.update();
          this.gameTitle.text = LOCALE_DATA[Store.instance.state.locale].DRAWING_GAME;
          break;
        case GAME_TYPE.JUMPING_GAME:
          this.gameThumbnail.texture = TextureFactory.instance.getSingleTexture('/textures/ui/game_2.png');
          this.gameThumbnail.texture.update();
          this.gameTitle.text = LOCALE_DATA[Store.instance.state.locale].JUMPING_GAME;
          break;
        default:
          break;
      }
    }
  }
}

class MultiplayRoom extends Scene {
  private players: Record<string, PlayerState> = {};
  private multiplayerStateStage: MultiplayerStateStage;
  private state: MULTIPLAY_ROOM_STATE = MULTIPLAY_ROOM_STATE.WAIT;

  private targetWidth: number;
  private scaffoldings: Array<MultiplayScaffolding>;
  private backgrounds: Array<Sprite>;
  public lastScaffoldingY: number = 0;
  private isBgmPlaying: boolean = false;
  private jumpGamePlayers: Record<string, JumpGamePlayer> = {};
  private progress?: JumpingGameProgress;
  private startTimer: number = 210;
  private vanishTitle: VanishTitle;
  private cutway: BlackFrameCutAway;
  private gameEndDt: number = 120;
  private emotions: Array<Emotion> = [];
  private emotionLastDT: number = Date.now();
  private resultPopup: ResultPopUp;

  constructor(game: Game) {
    super(game);

    this.backgrounds = [];
    this.scaffoldings = [];
    this.targetWidth = 1080;
    this.registerPeerHandler();

    this.camera.positionChasingCoefficient = 0.15;
    this.camera.zoomChasingCoefficient = 0.1;
    this.camera.setOffset({ x: 0, y: 0 });
    this.camera.setBoundary({
      min: { x: 0, y: 0 },
      max: { x: this.targetWidth, y: 1920 },
    });
    this.camera.setTargetPosition({ x: 540, y: 960 }, true);

    this.uiContainer.sortableChildren = true;
    this.multiplayerStateStage = new MultiplayerStateStage();
    this.multiplayerStateStage.players = this.players;
    this.multiplayerStateStage.on('left', () => {
      if (this.isHost) {
        const gameType =
          this.players[PeerStore.instance.peer.code!].type === GAME_TYPE.DRAWING_GAME
            ? GAME_TYPE.JUMPING_GAME
            : GAME_TYPE.DRAWING_GAME;
        for (const code in this.players) {
          this.players[code].type = gameType;
          if (code !== PeerStore.instance.peer.code) {
            this.players[code].state = PLAYER_STATE.WAIT;
          }
        }
        PeerStore.instance.peer.broadcast({ command: 'setPlayers', data: this.players });
        this.multiplayerStateStage.updateState();
      }
    });
    this.multiplayerStateStage.on('right', () => {
      if (this.isHost) {
        const gameType =
          this.players[PeerStore.instance.peer.code!].type === GAME_TYPE.DRAWING_GAME
            ? GAME_TYPE.JUMPING_GAME
            : GAME_TYPE.DRAWING_GAME;
        for (const code in this.players) {
          this.players[code].type = gameType;
          if (code !== PeerStore.instance.peer.code) {
            this.players[code].state = PLAYER_STATE.WAIT;
          }
        }
        PeerStore.instance.peer.broadcast({ command: 'setPlayers', data: this.players });
        this.multiplayerStateStage.updateState();
      }
    });
    this.multiplayerStateStage.on('ready', async () => {
      if (this.isHost) {
        try {
          const isLoaded: boolean = await loadGoogleAdvertisement();
          if (isLoaded) {
            await showGoogleAdvertisement();
          }
        } catch (e) {
          console.log(e);
        }
        this.state = MULTIPLAY_ROOM_STATE.LOADING_INITIALIZE;
        PeerStore.instance.peer.broadcast({ command: 'setRoomState', data: MULTIPLAY_ROOM_STATE.LOADING_INITIALIZE });
      } else {
        if (!this.players[PeerStore.instance.peer.code!].adViewed) {
          this.players[PeerStore.instance.peer.code!].adViewed = true;
          try {
            const isLoaded: boolean = await loadGoogleAdvertisement();
            if (isLoaded) {
              await showGoogleAdvertisement();
            }
          } catch (e) {
            console.log(e);
          }
        }

        if (this.players[PeerStore.instance.peer.code!].state === PLAYER_STATE.WAIT) {
          this.players[PeerStore.instance.peer.code!].state = PLAYER_STATE.READY;
        } else {
          this.players[PeerStore.instance.peer.code!].state = PLAYER_STATE.WAIT;
        }
        PeerStore.instance.peer.broadcast({
          command: 'changeState',
          data: {
            state: this.players[PeerStore.instance.peer.code!].state,
            adViewed: this.players[PeerStore.instance.peer.code!].adViewed,
          },
        });
        this.multiplayerStateStage.updateState();
      }
    });
    this.stage.addChild(this.multiplayerStateStage);

    this.vanishTitle = new VanishTitle('gaegu');
    this.vanishTitle.position.set(540, 960);
    this.uiContainer.addChild(this.vanishTitle);

    this.cutway = new BlackFrameCutAway();
    this.cutway.setSize(this.game.width, this.game.height);

    this.resultPopup = new ResultPopUp();
    this.resultPopup.visible = false;
    this.resultPopup.zIndex = 2;
    this.resultPopup.x = this.game.width / 2;
    this.resultPopup.y = this.game.height / 2;
    this.resultPopup.on('retry', () => {
      this.resultPopup.visible = false;
      this.state = MULTIPLAY_ROOM_STATE.WAIT;
      this.backgrounds = [];
      this.scaffoldings = [];
      this.startTimer = 210;
      this.stage.removeChildren();
      this.stage.addChild(this.multiplayerStateStage);
      this.uiContainer.removeChildren();
      this.uiContainer.addChild(this.vanishTitle);
      this.uiContainer.addChild(this.resultPopup);
      this.camera.setOffset({ x: 0, y: 0 });
      this.camera.setBoundary({
        min: { x: 0, y: 0 },
        max: { x: this.targetWidth, y: 1920 },
      });
      this.camera.setTargetPosition({ x: 540, y: 960 }, true);
      for (const code in this.players) {
        if (this.isHost) {
          if (code !== PeerStore.instance.peer.code) {
            this.players[code].state = PLAYER_STATE.WAIT;
          }
        } else {
          if (code === PeerStore.instance.peer.code) {
            this.players[code].state = PLAYER_STATE.WAIT;
          }
        }
      }
      this.multiplayerStateStage.updateState();
    });
    this.resultPopup.on('home', () => {
      PeerStore.instance.peer.close();
      PeerStore.instance.peer.destroy();
      this.game.changeScene(MainScene);
    });
    this.resultPopup.scale.set(this.camera.zoom);
    this.uiContainer.addChild(this.resultPopup);
  }

  public generateScaffolding(
    position: Point,
    brokenable: boolean,
    isBottom: boolean = false,
    isStatic: boolean = true,
    isLast: boolean = false,
    seed: string = ''
  ): void {
    const scaffolding = new MultiplayScaffolding({
      position,
      brokenable,
      range: [0, this.targetWidth],
      static: isStatic,
      isBottom,
      isLast,
      seed,
    });

    this.scaffoldings.push(scaffolding);

    this.stage.addChild(scaffolding.container);
    scaffolding.update(1);
  }

  private registerPeerHandler(): void {
    const seed = this.isHost ? generateCode(10) : '';
    this.players[PeerStore.instance.peer.code!] = {
      name: Store.instance.data.playername,
      state: this.isHost ? PLAYER_STATE.READY : PLAYER_STATE.WAIT,
      type: GAME_TYPE.JUMPING_GAME,
      initialized: false,
      seed,
      updatedAt: 0,
      adViewed: false,
    };
    for (const code in PeerStore.instance.peer.peers) {
      this.players[code] = {
        name: '',
        state: PLAYER_STATE.WAIT,
        type: GAME_TYPE.JUMPING_GAME,
        initialized: false,
        seed,
        updatedAt: 0,
        adViewed: false,
      };
    }

    // Register Peer Listener
    PeerStore.instance.peer.on('message', this.onMessage);
    PeerStore.instance.peer.on('disconnected', () => {
      PeerStore.instance.peer.close();
      PeerStore.instance.peer.destroy();
      this.game.changeScene(MainScene);
      message.error(Store.instance.state.locale === 'en-US' ? 'Lost Connection...' : '연결이 끊겼다 니니..');
    });
    this.emitEntered();
  }

  public get isHost(): boolean {
    return PeerStore.instance.peer.isHost;
  }

  public emotion(name: string, frame: number): void {
    const emotion = new Emotion();

    emotion.setEmotion(name, frame);
    emotion.position.set(0, this.game.height / 2);

    this.uiContainer.addChild(emotion);

    this.emotions.push(emotion);
  }

  public update(elapsed: number): void {
    super.update(elapsed);

    this.vanishTitle.scale.set(this.camera.zoom);
    this.vanishTitle.y = this.game.height / 2;
    this.vanishTitle.x = this.game.width / 2;
    this.vanishTitle.update(elapsed);

    let heightSum = 0;
    for (let i = this.emotions.length - 1; i >= 0; i--) {
      const emotion = this.emotions[i];
      emotion.position.x = this.game.width - emotion.width;
      emotion.position.y += (this.game.height / 2 - heightSum - emotion.position.y) * 0.1;
      emotion.update(elapsed);
      heightSum += emotion.height;

      if (emotion.dt > 120) {
        this.emotions.splice(i, 1);
        this.uiContainer.removeChild(emotion);
        emotion.destroy();
      }
    }

    if (this.state === MULTIPLAY_ROOM_STATE.WAIT) {
      this.multiplayerStateStage.update(elapsed);
      if (
        !this.isHost &&
        !this.isAllPlayerInitialized &&
        Date.now() - this.players[PeerStore.instance.peer.code!].updatedAt > 1000
      ) {
        console.log('emit');
        this.emitEntered();
      }
      return;
    } else if (this.state === MULTIPLAY_ROOM_STATE.LOADING_INITIALIZE) {
      this.state = MULTIPLAY_ROOM_STATE.LOADING;
      this.uiContainer.addChild(this.cutway);

      this.cutway.setFlow(linear, 0, this.cutway.duration);
      this.cutway.emitter.once('done', () => {
        this.cutway.setFlow(linear, this.cutway.duration, 0);
        this.cutway.emitter.once('done', () => {
          this.state = MULTIPLAY_ROOM_STATE.LOADED;
          this.uiContainer.removeChild(this.cutway);

          if (this.players[PeerStore.instance.peer.code!].type === GAME_TYPE.DRAWING_GAME) {
          } else {
            if (!this.isBgmPlaying) {
              sound.play('/audio/bgm_stage_3.mp3', { loop: true, volume: Store.instance.state.volume as number });
              this.isBgmPlaying = true;
            }
          }
        });

        logging(
          LOG_EVENT.GAME_START,
          LOG_SCENE.MULTIPLAY_SCENE,
          Store.instance.data.playername,
          window.navigator.userAgent
        );
        this.createEmotionUI();

        // Map Initialize
        if (this.players[PeerStore.instance.peer.code!].type === GAME_TYPE.DRAWING_GAME) {
        } else if (this.players[PeerStore.instance.peer.code!].type === GAME_TYPE.JUMPING_GAME) {
          const random = seedrandom(this.players[PeerStore.instance.peer.code!].seed || `${Math.random()}`);

          this.progress = new JumpingGameProgress(
            Object.keys(this.players).map((code) => ({
              code,
              name: this.players[code].name,
            }))
          );
          for (const code in this.players) {
            this.progress.update(this.game.width, this.game.height, 0, code);
            this.progress.ninimos[code].y = this.game.height - 100;
          }
          this.uiContainer.addChild(this.progress);

          for (let i = 1; i <= 3; i++) {
            const sprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/bg/bg_jumping.png'));
            sprite.x = 0;
            sprite.y = 500 - i * 1920;
            this.backgrounds.push(sprite);
            this.stage.addChild(sprite);
          }

          const lastBg = new Sprite(TextureFactory.instance.getSingleTexture('/textures/bg/bg_jumping_last.png'));
          lastBg.x = 0;
          this.stage.addChild(lastBg);

          this.generateScaffolding({ x: 0, y: 0 }, false, true, true, false, `${random()}`);

          const length = 84; // 120
          for (let i = 0; i < length; i++) {
            if (i === length - 1) {
              this.generateScaffolding({ x: 0, y: (i + 1) * -300 }, false, false, true, true, `${random()}`);
              this.lastScaffoldingY = (i + 1) * -300 + 10;
            } else {
              const width = this.targetWidth / 5 + (random() * this.targetWidth) / 3;
              this.generateScaffolding(
                { x: random() * (this.targetWidth - width), y: (i + 1) * -300 },
                i % 2 === 0 ? (random() < 0.5 ? true : false) : false,
                false,
                true,
                false,
                `${random()}`
              );
            }
          }
          lastBg.y = this.scaffoldings[this.scaffoldings.length - 1].position.y - 630;

          // Player...
          let i = 0;
          for (let code in this.players) {
            this.jumpGamePlayers[code] = new JumpGamePlayer(
              code === PeerStore.instance.peer.code!,
              this.players[code].name
            );
            this.jumpGamePlayers[code].position = { x: i === 0 ? 270 : 810, y: 0 };
            this.jumpGamePlayers[code].update(elapsed);
            this.stage.addChild(this.jumpGamePlayers[code].container);
            this.jumpGamePlayers[code].on('jump', () => {
              PeerStore.instance.peer.broadcast({
                command: 'jump',
                data: {
                  velocity: this.jumpGamePlayers[code].velocity,
                  position: this.jumpGamePlayers[code].position,
                },
              });
            });
            i++;
          }

          this.camera.positionChasingCoefficient = 0.15;
          this.camera.setBoundary({
            min: { x: 0, y: lastBg.y },
            max: { x: this.targetWidth, y: 550 },
          });
          this.camera.zoomChasingCoefficient = 0.1;
          this.camera.setOffset({ x: 0, y: 0 });
          this.camera.setTargetPosition(this.jumpGamePlayers[PeerStore.instance.peer.code!].position);
        }
      });
    } else if (this.state === MULTIPLAY_ROOM_STATE.LOADING) {
      this.cutway.update(elapsed);
    } else if (this.state === MULTIPLAY_ROOM_STATE.LOADED) {
      if (this.startTimer >= 210 && this.startTimer - elapsed < 210) {
        if (this.isHost) {
          PeerStore.instance.peer.broadcast({
            command: 'counting',
            data: 3,
          });
          this.vanishTitle.setTitle('3');
          this.vanishTitle.initialize();
        }
      } else if (this.startTimer >= 140 && this.startTimer - elapsed < 140) {
        if (this.isHost) {
          PeerStore.instance.peer.broadcast({
            command: 'counting',
            data: 2,
          });
          this.vanishTitle.setTitle('2');
          this.vanishTitle.initialize();
        }
      } else if (this.startTimer >= 70 && this.startTimer - elapsed < 70) {
        if (this.isHost) {
          PeerStore.instance.peer.broadcast({
            command: 'counting',
            data: 1,
          });
          this.vanishTitle.setTitle('1');
          this.vanishTitle.initialize();
        }
      } else if (this.startTimer - elapsed < 0) {
        this.state = MULTIPLAY_ROOM_STATE.PLAYING;
        if (this.isHost) {
          PeerStore.instance.peer.broadcast({
            command: 'gameStart',
          });
          this.vanishTitle.setTitle('Start!');
          this.vanishTitle.initialize();
        }
      }
      this.startTimer -= elapsed;
    } else if (this.state === MULTIPLAY_ROOM_STATE.PLAYING) {
      if (this.players[PeerStore.instance.peer.code!]?.type === GAME_TYPE.DRAWING_GAME) {
        // Drawing Game Logic...
      } else if (this.players[PeerStore.instance.peer.code!]?.type === GAME_TYPE.JUMPING_GAME) {
        // Jump Game Logic...
        for (let code in this.players) {
          this.jumpGamePlayers[code].velocity.y += 0.6 * elapsed;
          if (this.jumpGamePlayers[code].velocity.x > 0.1 * elapsed) {
            this.jumpGamePlayers[code].velocity.x -= 0.1 * elapsed;
          } else if (this.jumpGamePlayers[code].velocity.x < -0.1 * elapsed) {
            this.jumpGamePlayers[code].velocity.x += 0.1 * elapsed;
          } else {
            this.jumpGamePlayers[code].velocity.x = 0;
          }

          if (this.jumpGamePlayers[code].position.x + this.jumpGamePlayers[code].velocity.x < 0) {
            this.jumpGamePlayers[code].position.x = 0;

            if (this.jumpGamePlayers[code].velocity.x < 0) {
              this.jumpGamePlayers[code].velocity.x *= -1;
            }
          } else if (this.jumpGamePlayers[code].position.x + this.jumpGamePlayers[code].velocity.x > this.targetWidth) {
            this.jumpGamePlayers[code].position.x = this.targetWidth;

            if (this.jumpGamePlayers[code].velocity.x > 0) {
              this.jumpGamePlayers[code].velocity.x *= -1;
            }
          }

          const trajectory: Line = [
            { x: this.jumpGamePlayers[code].position.x, y: this.jumpGamePlayers[code].position.y },
            {
              x: this.jumpGamePlayers[code].position.x + this.jumpGamePlayers[code].velocity.x * elapsed,
              y: this.jumpGamePlayers[code].position.y + this.jumpGamePlayers[code].velocity.y * elapsed,
            },
          ];
          if (this.jumpGamePlayers[code].velocity.y > 0) {
            for (let i = this.scaffoldings.length - 1; i >= 0; i--) {
              const scaffolding = this.scaffoldings[i];

              const dt = intersectionLL(trajectory, [
                { x: scaffolding.position.x, y: scaffolding.position.y },
                { x: scaffolding.position.x + scaffolding.width, y: scaffolding.position.y },
              ]);

              if (dt >= 0 && dt <= 1) {
                if (scaffolding.brokenable) {
                  scaffolding.vanish();
                } else {
                  this.jumpGamePlayers[code].velocity.y = 0;
                  this.jumpGamePlayers[code].velocity.x = scaffolding.velocity.x;
                  this.jumpGamePlayers[code].position.y = scaffolding.position.y;

                  if (this.jumpGamePlayers[code].state !== 'IDLE' && this.jumpGamePlayers[code].state !== 'CLEAR') {
                    this.jumpGamePlayers[code].isSuperJump = false;
                    this.jumpGamePlayers[code].trigger(`LANDING`);
                    if (
                      this.jumpGamePlayers[code].position.y < this.lastScaffoldingY &&
                      this.jumpGamePlayers[code].state !== 'CLEAR'
                    ) {
                      this.jumpGamePlayers[code].trigger(`CLEAR`);
                      this.resultPopup.visible = true;
                      this.jumpGamePlayers[code].off('jump');
                      this.jumpGamePlayers[code].update(1);
                      if (code === PeerStore.instance.peer.code!) {
                        this.resultPopup.changeState('WIN');
                      } else {
                        this.resultPopup.changeState('LOSE');
                      }
                      // this.vanishTitle.setTitle(`${this.players[code].name}\nWIN`);
                      // this.vanishTitle.initialize();

                      logging(
                        LOG_EVENT.GAME_CLEAR,
                        LOG_SCENE.MULTIPLAY_SCENE,
                        Store.instance.data.playername,
                        window.navigator.userAgent
                      );

                      return;
                    }
                  }
                  break;
                }
              }
            }
          }

          if (this.jumpGamePlayers[code].position.y <= this.scaffoldings[this.scaffoldings.length - 1].position.y) {
            this.scaffoldings[this.scaffoldings.length - 1].container.zIndex = Infinity;
          }

          this.jumpGamePlayers[code].update(elapsed);
          if (this.progress) {
            this.progress.update(
              this.game.width,
              this.game.height,
              this.jumpGamePlayers[code].position.y / this.lastScaffoldingY,
              code
            );
          }
        }

        for (const scaffolding of this.scaffoldings) {
          scaffolding.update(elapsed);
        }

        let maxY = -Infinity;
        let minY = Infinity;
        for (const point of this.camera.viewport) {
          if (maxY < point.y) {
            maxY = point.y;
          }
          if (minY > point.y) {
            minY = point.y;
          }
        }

        for (const background of this.backgrounds) {
          if (background.y > maxY) {
            background.y -= 1920 * this.backgrounds.length;
          }
          if (background.y + background.height < minY) {
            background.y += 1920 * this.backgrounds.length;
          }
        }
      }
    } else if (this.state === MULTIPLAY_ROOM_STATE.END) {
      if (this.gameEndDt >= elapsed) {
        this.gameEndDt -= elapsed;
      } else {
        this.gameEndDt = 0;
        this.state = MULTIPLAY_ROOM_STATE.CLOSING;
        this.game.changeScene(MultiplayRoom);
      }
    }
  }

  public get isAllPlayerInitialized(): boolean {
    return Object.values(this.players).every((player) => player.initialized);
  }

  private emitEntered(): void {
    if (this.isHost) {
      this.players[PeerStore.instance.peer.code!].initialized = true;
    } else {
      this.players[PeerStore.instance.peer.code!].updatedAt = Date.now();
      PeerStore.instance.peer.broadcast({
        command: 'entered',
        data: {
          code: PeerStore.instance.peer.code!,
          name: Store.instance.data.playername,
        },
      });
    }
    if (this.multiplayerStateStage) {
      this.multiplayerStateStage.updateState();
    }
  }

  private entered(code: string, name: string): void {
    if (!this.players[code]) {
      this.players[code] = {
        name,
        state: PLAYER_STATE.WAIT,
        type: GAME_TYPE.JUMPING_GAME,
        initialized: false,
        seed: this.players[PeerStore.instance.peer.code!].seed,
        updatedAt: 0,
        adViewed: false,
      };
    }
    this.players[code].name = name;
    this.players[code].initialized = true;
    if (this.isHost) {
      PeerStore.instance.peer.broadcast({ command: 'setPlayers', data: this.players });
    }
    if (this.multiplayerStateStage) {
      this.multiplayerStateStage.updateState();
    }
  }

  public createEmotionUI(): void {
    const textures = TextureFactory.instance.load(EmotionData);
    const backgroundSprite = new Sprite(
      TextureFactory.instance.getSingleTexture('/textures/ui/emotion_background.png')
    );
    backgroundSprite.anchor.set(0.5, 0.5);
    backgroundSprite.scale.set(this.camera.zoom * 1.3);
    backgroundSprite.position.set(this.game.width / 2, this.game.height - 128 * this.camera.zoom);
    this.uiContainer.addChild(backgroundSprite);

    const title = new Text(LOCALE_DATA[Store.instance.state.locale].EMOTION, {
      fontSize: Math.ceil(64 * this.camera.zoom),
      fontFamily: 'gaegu',
      fontStyle: 'normal',
      letterSpacing: -4,
      fill: '#4a282f',
      stroke: 0xffffff,
      strokeThickness: Math.ceil(16 * this.camera.zoom),
      lineJoin: 'round',
      padding: 8,
    });
    title.anchor.set(0.5);
    title.position.set(this.game.width / 2, this.game.height - 270 * this.camera.zoom);
    this.uiContainer.addChild(title);

    for (let i = 0; i < 14; i++) {
      const container = new Container();
      const emotion = new Sprite(textures[i]);
      const graphics = new Graphics();
      graphics.beginFill(0x000000, 0.3);
      graphics.drawCircle(0, 0, 50);
      graphics.endFill();
      emotion.anchor.set(0.5);
      container.scale.set(this.camera.zoom);
      container.x = this.game.width / 2 - 3 * 105 * this.camera.zoom + (i % 7) * 105 * this.camera.zoom;
      container.y = this.game.height - 180 * this.camera.zoom + Math.floor(i / 7) * 105 * this.camera.zoom;
      container.addChild(graphics);
      container.addChild(emotion);
      container.interactive = true;
      container.buttonMode = true;
      container.on('pointerup', (e: InteractionEvent) => {
        e.stopPropagation();
        this.emitEmotion(i);
      });

      this.uiContainer.addChild(container);
    }
  }

  public emitEmotion(frame: number): void {
    if (Date.now() - this.emotionLastDT > 200) {
      this.emotionLastDT = Date.now();
      this.emotion(this.players[PeerStore.instance.peer.code!].name, frame);
      PeerStore.instance.peer.broadcast({
        command: 'emotion',
        data: frame,
      });
    }
  }

  private setPlayers(players: Record<string, PlayerState>): void {
    this.players = players;
    this.multiplayerStateStage.players = players;
    if (this.multiplayerStateStage) {
      this.multiplayerStateStage.updateState();
    }
  }

  private changeState(code: string, data: Partial<PlayerState>): void {
    if (data.state !== undefined) {
      this.players[code].state = data.state;
    }
    if (data.adViewed !== undefined) {
      this.players[code].adViewed = data.adViewed;
    }
    if (this.multiplayerStateStage) {
      this.multiplayerStateStage.updateState();
    }
  }

  public onMessage = (code: string, message: any): void => {
    switch (message.command) {
      case 'entered':
        this.entered(message.data.code, message.data.name);
        break;
      case 'setRoomState':
        this.state = message.data;
        break;
      case 'changeState':
        this.changeState(code, message.data);
        break;
      case 'setPlayers':
        this.setPlayers(message.data);
        break;
      case 'jump':
        this.jump(code, message.data);
        break;
      case 'gameStart':
        this.vanishTitle.setTitle(`Start!`);
        this.vanishTitle.initialize();
        break;
      case 'emotion':
        this.emotion(this.players[code].name, message.data);
        break;
      case 'counting':
        this.vanishTitle.setTitle(`${message.data}`);
        this.vanishTitle.initialize();
        break;
      default:
        console.log('unknown command');
        return;
    }
  };

  private jump(code: string, { velocity, position }: { velocity: Point; position: Point }): void {
    if (this.jumpGamePlayers[code]) {
      this.jumpGamePlayers[code].velocity.x = velocity.x;
      this.jumpGamePlayers[code].velocity.y = velocity.y;
      this.jumpGamePlayers[code].position.x = position.x;
      this.jumpGamePlayers[code].position.y = position.y;

      if (this.jumpGamePlayers[code].velocity.x < 0) {
        this.jumpGamePlayers[code].animatedSprite.scale.x =
          Math.abs(this.jumpGamePlayers[code].animatedSprite.scale.x) * -1;
      } else if (this.jumpGamePlayers[code].velocity.x > 0) {
        this.jumpGamePlayers[code].animatedSprite.scale.x =
          Math.abs(this.jumpGamePlayers[code].animatedSprite.scale.x) * 1;
      }
    }
  }

  public async onBegin(): Promise<void> {}
  // GAME LOGIC

  //
  public static preload = async (callback: (currentValue: number, maxValue: number) => void): Promise<void> => {
    logging(LOG_EVENT.VISIT, LOG_SCENE.MULTIPLAY_SCENE, Store.instance.data.playername, window.navigator.userAgent);
    const assets: Array<string> = [
      '/audio/bgm_stage_3.mp3',
      '/audio/crash.mp3',
      '/audio/heartlose.mp3',
      '/audio/heartget.mp3',
      '/audio/getting_item.mp3',
      '/audio/jump.mp3',
      '/audio/spring.mp3',
      '/audio/pung.mp3',
      '/audio/bgm_stage_1.mp3',
      '/audio/getting_item.mp3',
      '/audio/heartget.mp3',
      '/audio/heartlose.mp3',
      '/audio/knife.mp3',
      '/audio/vegetable_die.mp3',

      '/textures/bg/bg_drawing.png',
      '/textures/ui/mouse.png',
      '/textures/ui/slot.png',
      '/textures/ui/emotion.webp',
      '/textures/objects/ninimo/draw_1.webp',
      '/textures/objects/ninimo/draw_2.webp',
      '/textures/objects/monster/pumpkin.webp',
      '/textures/objects/monster/watermelon.webp',
      '/textures/objects/monster/apple_1.webp',
      '/textures/objects/monster/apple_2.webp',
      '/textures/objects/monster/eggplant.webp',
      '/textures/objects/monster/cucumber.webp',
      '/textures/pattern/0.png',
      '/textures/pattern/1.png',
      '/textures/pattern/2.png',
      '/textures/pattern/3.png',
      '/textures/pattern/4.png',
      '/textures/objects/etc/bread.png',
      '/textures/objects/etc/scaffolding_1.png',
      '/textures/objects/etc/scaffolding_2.png',
      '/textures/objects/etc/scaffolding_3.png',
      '/textures/objects/etc/scaffolding_4.png',
      '/textures/objects/etc/scaffolding_5.png',
      '/textures/objects/etc/scaffolding_6.png',
      '/textures/objects/etc/scaffolding_7.png',
      '/textures/objects/etc/scaffolding_8.png',
      '/textures/objects/etc/scaffolding_9.png',
      '/textures/objects/etc/heart.png',
      '/textures/objects/etc/spring.png',
      '/textures/objects/ninimo/jump.webp',
      '/textures/bg/bg_jumping.png',
      '/textures/bg/bg_jumping_last.png',
      '/textures/ui/game_1.png',
      '/textures/ui/game_2.png',
      '/textures/ui/btn_arrow.png',
    ];

    let maxLoaded = 1;
    for (const asset of assets) {
      if (!Loader.shared.resources[asset]) {
        Loader.shared.add(asset, asset);
        maxLoaded++;
      }
    }

    let loaded: number = 1;
    return new Promise((resolve) => {
      Loader.shared.onLoad.add(() => {
        callback(++loaded, maxLoaded);
      });
      Loader.shared.onComplete.once(() => {
        resolve();
      });
      Loader.shared.load();
    });
  };

  protected pointerup = (event: InteractionEvent) => {
    if (this.game.paused) return;

    if (
      this.players[PeerStore.instance.peer.code!] &&
      this.players[PeerStore.instance.peer.code!].type === GAME_TYPE.JUMPING_GAME &&
      this.state === MULTIPLAY_ROOM_STATE.PLAYING // TODO: Change
    ) {
      if (this.jumpGamePlayers[PeerStore.instance.peer.code!].canJump) {
        this.jumpGamePlayers[PeerStore.instance.peer.code!].next();
      }
    }
  };

  public destroy(): void {
    super.destroy();
  }
}

export default MultiplayRoom;
